import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Menu, X, ShoppingCart, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';

import CartDropdown from './header/CartDropdown';
import MobileMenu from './header/MobileMenu';
import EcosystemMenu from './header/EcosystemMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NAV_ITEMS = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  { path: '/contact', label: 'Contact' },
];

const ECOSYSTEM_ITEMS = [
  { path: '/ecosystem', label: 'Our Ecosystem' },
  { path: '/shop', label: 'Farmers Market' },
  { path: '/investment', label: 'Investment' },
  { path: '/wholesale', label: 'Wholesale' },
  { path: '/track-order', label: 'Track Order' },
];

/**
 * Header orchestrator — holds auth/cart state and delegates rendering to:
 *   - EcosystemMenu (desktop dropdown)
 *   - CartDropdown  (desktop mini-cart)
 *   - MobileMenu    (slide-down mobile drawer)
 */
const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [cartBounce, setCartBounce] = useState(false);
  const [showMiniCart, setShowMiniCart] = useState(false);
  const [customerUser, setCustomerUser] = useState(null);
  const [investorUser, setInvestorUser] = useState(null);
  const [ecosystemOpen, setEcosystemOpen] = useState(false);

  const location = useLocation();
  const { cart, getCartCount, getCartTotal, recentlyAdded, hasItems, removeFromCart, updateQuantity } = useCart();

  // Sync auth state with localStorage on route change
  useEffect(() => {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try { setCustomerUser(JSON.parse(userData)); } catch { setCustomerUser(null); }
    } else {
      setCustomerUser(null);
    }
    const investorData = localStorage.getItem('investor_data');
    if (investorData) {
      try { setInvestorUser(JSON.parse(investorData)); } catch { setInvestorUser(null); }
    } else {
      setInvestorUser(null);
    }
  }, [location]);

  const handleCustomerLogout = () => {
    localStorage.removeItem('user_data');
    localStorage.removeItem('investor_data');
    axios.post(`${BACKEND_URL}/api/auth/logout`).catch((err) => console.warn('logout failed:', err?.message || err));
    setCustomerUser(null);
    setInvestorUser(null);
  };

  // Bounce on add-to-cart
  useEffect(() => {
    if (recentlyAdded) {
      setCartBounce(true);
      setShowMiniCart(false);
      const timer = setTimeout(() => setCartBounce(false), 600);
      return () => clearTimeout(timer);
    }
  }, [recentlyAdded]);

  const isActive = (path) => location.pathname === path;
  const isEcosystemActive = ECOSYSTEM_ITEMS.some((item) => isActive(item.path));
  const cartCount = getCartCount();
  const cartTotal = getCartTotal();

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <nav className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group" data-testid="header-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_green-harvest-29/artifacts/eijdqkpw_unnamed%20%281%29.jpg"
              alt="Daye Farm Ltd."
              className="h-14 w-14 rounded-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="flex flex-col">
              <span className="text-xl font-bold text-gray-900">Daye Farm Ltd.</span>
              <span className="text-xs text-green-600">Nature's Harvest</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {NAV_ITEMS.slice(0, 2).map((item) => (
              <Link key={item.path} to={item.path}>
                <Button
                  variant="ghost"
                  className={`${
                    isActive(item.path) ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700 hover:text-green-700 hover:bg-green-50'
                  } transition-all duration-300`}
                  data-testid={`header-nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Button>
              </Link>
            ))}

            <EcosystemMenu
              ecosystemItems={ECOSYSTEM_ITEMS}
              isActive={isActive}
              isEcosystemActive={isEcosystemActive}
              customerUser={customerUser}
              investorUser={investorUser}
              onLogout={handleCustomerLogout}
            />

            {NAV_ITEMS.slice(2).map((item) => (
              <Link key={item.path} to={item.path}>
                <Button
                  variant="ghost"
                  className={`${
                    isActive(item.path) ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700 hover:text-green-700 hover:bg-green-50'
                  } transition-all duration-300`}
                  data-testid={`header-nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Button>
              </Link>
            ))}

            <CartDropdown
              cart={cart}
              cartCount={cartCount}
              cartTotal={cartTotal}
              hasItems={hasItems}
              recentlyAdded={recentlyAdded}
              cartBounce={cartBounce}
              showMiniCart={showMiniCart}
              setShowMiniCart={setShowMiniCart}
              removeFromCart={removeFromCart}
              updateQuantity={updateQuantity}
            />
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <Link to="/cart" className="relative p-2" data-testid="header-mobile-cart-link">
              <ShoppingCart className={`h-6 w-6 ${hasItems ? 'text-green-600' : 'text-gray-700'} ${cartBounce ? 'animate-bounce' : ''}`} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-lg">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-300"
              aria-label="Toggle menu"
              data-testid="header-mobile-menu-toggle"
            >
              {isOpen ? <X className="h-6 w-6 text-gray-900" /> : <Menu className="h-6 w-6 text-gray-900" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <MobileMenu
            navItems={NAV_ITEMS}
            ecosystemItems={ECOSYSTEM_ITEMS}
            ecosystemOpen={ecosystemOpen}
            setEcosystemOpen={setEcosystemOpen}
            isActive={isActive}
            isEcosystemActive={isEcosystemActive}
            customerUser={customerUser}
            investorUser={investorUser}
            cartCount={cartCount}
            hasItems={hasItems}
            onCloseMenu={() => setIsOpen(false)}
            onLogout={handleCustomerLogout}
          />
        )}
      </nav>

      {/* Mobile Recently Added Toast */}
      {recentlyAdded && (
        <div className="lg:hidden fixed bottom-4 left-4 right-4 bg-white rounded-lg shadow-xl border border-green-200 p-3 animate-in slide-in-from-bottom duration-300 z-50">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Added to cart!</p>
              <p className="text-xs text-gray-500 truncate">{recentlyAdded.name}</p>
            </div>
            <Link to="/cart" data-testid="header-mobile-cart-view-link">
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">View</Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
